import React, { useMemo } from 'react'
import { Layers } from 'lucide-react'

interface UmlAttribute {
  id: string
  name: string
  type: string
  visibility: 'public' | 'private' | 'protected'
}

interface UmlMethod {
  id: string
  name: string
  returnType: string
  parameters: string
  visibility: 'public' | 'private' | 'protected'
}

interface UmlClass {
  id: string
  name: string
  type: string
  x: number
  y: number
  width: number
  height: number
  attributes: UmlAttribute[]
  methods: UmlMethod[]
}

interface UmlRelationship {
  id: string
  fromClassId: string
  toClassId: string
  type: string
  label?: string
}

interface UMLDesignViewerProps {
  classes: UmlClass[]
  relationships: UmlRelationship[]
  className?: string
}

const RELATIONSHIP_COLORS: Record<string, string> = {
  inheritance: '#1d4ed8',
  composition: '#059669',
  aggregation: '#8b5cf6',
  association: '#f97316',
  dependency: '#0ea5e9'
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  inheritance: 'inherits',
  composition: 'composes',
  aggregation: 'aggregates',
  association: 'associates',
  dependency: 'depends'
}

const visibilitySymbol = (visibility: string) => {
  switch ((visibility || '').toLowerCase()) {
    case 'private':
      return '-'
    case 'protected':
      return '#'
    case 'public':
    default:
      return '+'
  }
}

const typeAccentClasses: Record<string, string> = {
  class: 'bg-gray-900 text-white',
  interface: 'bg-blue-600 text-white',
  abstract: 'bg-purple-600 text-white',
  actor: 'bg-amber-500 text-black',
  usecase: 'bg-teal-500 text-black'
}

function computeCanvasBounds(classes: UmlClass[]) {
  if (!classes.length) {
    return { width: 800, height: 600 }
  }

  const maxX = Math.max(...classes.map(cls => cls.x + cls.width))
  const maxY = Math.max(...classes.map(cls => cls.y + cls.height))

  return {
    width: Math.max(800, maxX + 200),
    height: Math.max(600, maxY + 200)
  }
}

const UMLDesignViewer: React.FC<UMLDesignViewerProps> = ({ classes, relationships, className = '' }) => {
  const preparedClasses = useMemo(() => {
    return classes.map(cls => ({
      ...cls,
      type: (cls.type || 'class').toLowerCase()
    }))
  }, [classes])

  const classMap = useMemo(() => {
    const map = new Map<string, UmlClass>()
    preparedClasses.forEach(cls => {
      map.set(cls.id, cls)
    })
    return map
  }, [preparedClasses])

  const sanitizedRelationships = useMemo(() => {
    return relationships
      .map(rel => ({
        id: rel.id,
        fromClassId: rel.fromClassId,
        toClassId: rel.toClassId,
        type: (rel.type || 'association').toLowerCase(),
        label: rel.label
      }))
      .filter(rel => classMap.has(rel.fromClassId) && classMap.has(rel.toClassId))
  }, [relationships, classMap])

  const canvasBounds = useMemo(() => computeCanvasBounds(preparedClasses), [preparedClasses])

  return (
    <div className={`relative bg-slate-100 border border-slate-200 rounded-xl overflow-hidden ${className}`}>
      <div className="absolute top-3 right-3 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-slate-600 shadow-sm">
        <span className="mr-3">Classes: {preparedClasses.length}</span>
        <span>Relationships: {sanitizedRelationships.length}</span>
      </div>

      <div className="relative overflow-auto" style={{ height: Math.min(Math.max(canvasBounds.height, 480), 960) }}>
        <div
          style={{
            width: canvasBounds.width,
            height: canvasBounds.height,
            position: 'relative'
          }}
        >
          {preparedClasses.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-slate-400">
                <Layers className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">No UML elements captured for this diagram.</p>
              </div>
            </div>
          )}

          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {sanitizedRelationships.map(rel => {
              const fromClass = classMap.get(rel.fromClassId)!
              const toClass = classMap.get(rel.toClassId)!

              const fromX = fromClass.x + fromClass.width / 2
              const fromY = fromClass.y + fromClass.height / 2
              const toX = toClass.x + toClass.width / 2
              const toY = toClass.y + toClass.height / 2

              const color = RELATIONSHIP_COLORS[rel.type] || '#4b5563'
              const label = rel.label || RELATIONSHIP_LABELS[rel.type] || rel.type

              return (
                <g key={rel.id}>
                  <line
                    x1={fromX}
                    y1={fromY}
                    x2={toX}
                    y2={toY}
                    stroke={color}
                    strokeWidth={2.5}
                    markerEnd="url(#uml-arrowhead)"
                  />
                  <text
                    x={(fromX + toX) / 2}
                    y={(fromY + toY) / 2 - 6}
                    fontSize="11"
                    fontWeight={600}
                    textAnchor="middle"
                    fill={color}
                    className="font-mono"
                  >
                    {label}
                  </text>
                </g>
              )
            })}

            <defs>
              <marker
                id="uml-arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#4b5563" />
              </marker>
            </defs>
          </svg>

          {preparedClasses.map(cls => {
            const accentClasses = typeAccentClasses[cls.type] || 'bg-slate-900 text-white'
            return (
              <div
                key={cls.id}
                className="absolute rounded-lg shadow-lg border border-slate-300 bg-white overflow-hidden"
                style={{
                  left: cls.x,
                  top: cls.y,
                  width: cls.width,
                  minWidth: 220
                }}
              >
                <div className={`px-4 py-3 ${accentClasses}`}>
                  <p className="text-sm font-semibold leading-tight break-words">{cls.name}</p>
                  <p className="text-[11px] uppercase tracking-widest opacity-80">{cls.type}</p>
                </div>
                <div className="px-4 py-3 space-y-4 bg-slate-50">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Attributes</p>
                    {cls.attributes.length ? (
                      <ul className="space-y-1">
                        {cls.attributes.map(attr => (
                          <li key={attr.id} className="text-xs text-slate-700 font-mono break-words">
                            {visibilitySymbol(attr.visibility || 'public')} {attr.name}: {attr.type}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No attributes defined</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Methods</p>
                    {cls.methods.length ? (
                      <ul className="space-y-1">
                        {cls.methods.map(method => (
                          <li key={method.id} className="text-xs text-slate-700 font-mono break-words">
                            {visibilitySymbol(method.visibility || 'public')} {method.name}({method.parameters}) : {method.returnType || 'void'}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No methods defined</p>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default UMLDesignViewer
